import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import NoDataTable from "../../../../components/noDataTable";
import { tokens } from "../../../../theme";
import { useTheme } from "@emotion/react";
import { useRestaurant } from "../../../../hooks/RestaurantContext";
import SortableItem from "../../../../components/sortableItem";
import Loading from "../../../../components/loading";
import Error from "../../../../components/Error";
import { updateCategoryPositions } from "../../../../features/categorySlice";
import ParagraphHighliter from "../../../../components/paragraphHighliter";
import { useTranslation } from "react-i18next";
import TableActions from "../../../../components/tableActions";

const ListViewCategory = ({
  handleClickOpen,
  handleModify,
  handleProducts,
  filteredCategories,
  setFilteredCategories,
  status,
  error,
  search,
  disableDrag = false,
  loadingStatus = "loadingFetchData",
  errorStatus = "fetchError",
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { restaurantId } = useRestaurant();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const mouseSensor = useSensor(MouseSensor);
  const keyboardSensor = useSensor(KeyboardSensor, {
    coordinateGetter: sortableKeyboardCoordinates,
  });
  // When drag reorder is disabled (list spans more than one page), don't
  // register any active sensors so the rows aren't draggable at all.
  const sensors = useSensors(
    ...(disableDrag ? [] : [mouseSensor, keyboardSensor])
  );

  const handleDragEnd = (event) => {
    if (disableDrag) return;
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = filteredCategories.findIndex(
        (item) => item._id === active.id
      );
      const newIndex = filteredCategories.findIndex(
        (item) => item._id === over.id
      );
      const newOrder = arrayMove(filteredCategories, oldIndex, newIndex);
      if (oldIndex === newIndex) return;
      setFilteredCategories(newOrder);
      const positions = newOrder.map((item, index) => ({
        categoryId: item._id,
        position: index + 1,
      }));

      dispatch(
        updateCategoryPositions({
          positions,
          restaurantId,
        })
      )
        .unwrap()
        .then(() => toast.success("Position modifiée avec succès"))
        .catch((error) => toast.error(error.message));
    }
  };

  return (
    <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
      <TableContainer>
        <Table
          sx={{
            backgroundColor: colors.primary[400],
          }}
        >
          <TableHead
            sx={{
              backgroundColor: colors.primary[700],
            }}
          >
            <TableRow>
              <TableCell sx={{ width: 70 }}>{t("table.number")}</TableCell>
              <TableCell sx={{ width: 100 }}>{t("table.image")}</TableCell>
              <TableCell>{t("table.name")}</TableCell>
              <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                {t("table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {status === loadingStatus ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Loading size={8} />
                </TableCell>
              </TableRow>
            ) : status === errorStatus ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <Error>{error}</Error>
                </TableCell>
              </TableRow>
            ) : (
              <SortableContext
                items={filteredCategories.map((item) => item._id)}
                strategy={verticalListSortingStrategy}
              >
                {filteredCategories && filteredCategories.length > 0 ? (
                  filteredCategories.map((item, index) => (
                    <SortableItem
                      key={item._id}
                      id={item._id}
                      disableDragArea={[3]}
                      growColumnIndex={2}
                    >
                      <>{index + 1}</>
                      <>
                        <img
                          src={`${process.env.REACT_APP_MEDIA_URL}/${item.image}`}
                          alt={item.name}
                          style={{
                            width: "48px",
                            height: "48px",
                            objectFit: "contain",
                          }}
                        />
                      </>
                      <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                        <ParagraphHighliter
                          title={item.name}
                          search={search}
                        />
                      </Box>
                      <>
                        <TableActions
                          actions={[
                            {
                              type: "delete",
                              color: "error",
                              label: t("common.delete"),
                              onClick: () => handleClickOpen(item._id),
                            },
                            {
                              type: "edit",
                              color: "warning",
                              label: t("common.edit"),
                              onClick: () => handleModify(item),
                            },
                            {
                              type: "view",
                              color: "info",
                              label: t("common.viewProducts"),
                              onClick: () => handleProducts(item._id, item.name),
                            },
                          ]}
                        />
                      </>
                    </SortableItem>
                  ))
                ) : (
                  <NoDataTable cols={4} />
                )}
              </SortableContext>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </DndContext>
  );
};

export default ListViewCategory;
